import {
  Button,
  Modal,
  ModalContent,
  ModalFooter,
  TextField
} from '@deque/cauldron-react';
import emailValidator from 'email-validator';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type HttpError } from '~/common/api-client';
import { useGlobalToast } from '~/common/contexts/globalToast';
import billingClient, {
  type EnterpriseMembers
} from '~/common/utils/billing-client/client-v2';
import styles from './EditEmailModal.css';

export interface EditEmailModalProps {
  member: EnterpriseMembers.AcceptedMember;
  enterpriseId: string;
  token: string;
  onClose: () => void;
  onSuccess: (newEmail: string) => void;
}

const EditEmailModal = ({
  member,
  enterpriseId,
  token,
  onClose,
  onSuccess
}: EditEmailModalProps) => {
  const { t } = useTranslation();
  const { setContents } = useGlobalToast();
  const [email, setEmail] = useState(member.email);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  // Move focus to the field when an error appears
  useEffect(() => {
    if (emailError) {
      fieldRef.current?.focus();
    }
  }, [emailError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setEmailError(t('Email is required'));
      return;
    }
    if (!emailValidator.validate(trimmed)) {
      setEmailError(t('Please enter a valid email address'));
      return;
    }
    if (trimmed === member.email.toLowerCase()) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      await billingClient.enterpriseMembers.updateEmail({
        token,
        enterpriseId,
        memberId: member.user_id,
        email: trimmed
      });
      onSuccess(trimmed);
    } catch (err) {
      const httpErr = err as HttpError;
      if (httpErr.status === 400 || httpErr.status === 409) {
        setEmailError(httpErr.message);
      } else {
        setContents(t('Something went wrong. Please try again.'), 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      heading={<span className={styles.modalHeader}>{t('Edit email')}</span>}
      onClose={onClose}
      show
    >
      <form onSubmit={handleSubmit} noValidate>
        <ModalContent className={styles.modalContent}>
          <TextField
            fieldRef={fieldRef}
            label={t('Email')}
            name="email"
            type="email"
            value={email}
            onChange={setEmail}
            error={emailError ?? undefined}
            autoComplete="off"
            required
          />
        </ModalContent>
        <ModalFooter>
          <Button variant="primary" type="submit" disabled={submitting}>
            {t('Save')}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('Cancel')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default EditEmailModal;
